/**
 * Supported types for URL param value casting.
 * @typedef {!(StringConstructor|NumberConstructor|BooleanConstructor|ObjectConstructor)} SupportedParamCastType
 * @typedef {(String|Number|Boolean|Object)} SupportedParamValueType
 */

/**
 * Global cached (query & hash) param values which can be re-used instead of parsing every time.
 * raw: The original input string before being parsed (eg location.search or location.hash), used to detect if we need to reparse.
 * params: The object containing parsed params (eg: { 'k1':['v1'], 'k2':['v2'], 'k3':['v3a','v3b',v3c'] }).
 */
const _cached = {
    query: {
        raw: null,
        params: null
    },
    hash: {
        raw: null,
        params: null
    }
};

function _cachedParse(cache, rawParamsStr) {
    // URL has not changed since last call, just use what we already have.
    if (cache.raw === rawParamsStr)
        return cache.params;
    // else need to re-parse.

    cache.raw = rawParamsStr;
    cache.params = _parse(rawParamsStr);

    return cache.params;
}
function _parse(input) {
    // Parse the input string (query/hash) and store as an array of key:[values,...] pairs.
    // The values are stored as an array to support multiple values: k1=v1&k1=v2&k1=v3 -> k1=[v1,v2,v3].
    return input // "k1=v1&k2=v2"
        .split('&') // Split on ampersands (['k1=v1', 'k2=v2']).
        .map((p) => {
            const pair =
                p.split('=') // Split to pairs 'k1=v1' -> ['k1','v1'].
                .map((x) => x.trim()); // Trim whitespace.

            // Make sure there is a pair and the first (key) is not empty.
            if (pair.length <= 0 || pair[0].length === 0) {
                return null;
            }

            // If only one element in the pair then no value was specified, add null value.
            if (pair.length === 1) {
                pair.push(null);
            }

            return pair;
        })
        .reduce((results, p) => {
            // Check if p is valid, it might be null if the param was invalid (see map above).
            if (p) {
                const key = p[0].toLowerCase(),
                      val = p[1];

                let values = results[key]; // Get existing values for the key.

                // No existing values for the key, yet.
                if (!values) {
                    values = results[key] = []; // Create empty array and set as values for the key in results.
                }

                // Add the value to the key's values (decodeURIComponent will stringify null => 'null' which we dont want).
                if (val === null) values.push(val);
                else {
                    const cleanVal = decodeURIComponent(val).replaceAll('+', ' ').trim(); // Manually convert '+' to space, not sure why decodeURIComponent does not do it automatically.
                    values.push(cleanVal);
                }
            }
            // else don't add the invalid param.

            return results;
        }, {});
}

function _filterCopy(params, key) {
    if (key)
        return { [key]: params[key] };
    else
        return { ...params };
}

function _mutate(params, mutator) {
    for (const key in params) {
        params[key] = mutator(params[key]);
    }
}

function _castValue(val, type) {
    switch (type) {
        case Number:
            return Number(val);

        case Boolean:
            return val === 'true' || (val !== '0' && val !== 'false' && Boolean(val));

        case Object:
            return val ? JSON.parse(val) : val;

        case String:
        default:
            return val;
    }
}

function _getParams(parsedKeyValues, key, opts) {
    // Argument overloading (opts) without key, ie get all params.
    if (typeof key === 'object' && !opts) {
        opts = key;
        key = null;
    }

    // Default opts.
    opts = {
        multival: true,
        cast: null,
        ...opts
    };

    // Copy the params and filter per the specified key (or all if no key specified).
    // A copy is necessary so we can mutate it without changing the global.
    const params = _filterCopy(parsedKeyValues, key); // { k1: [k1v1, k1v2], k2: [k2v1], ... }

    // If a cast is specified then attempt to cast all the values.
    if (opts.cast) {
        _mutate(params, vals => vals && vals.length ? vals.map(v => _castValue(v, opts.cast)) : null);
    }

    // If multival is falsey then we discard multiple values.
    if (!opts.multival) {
        _mutate(params, vals => vals && vals.length ? vals[0] : null); // [1,2,3] => 1
    }

    // If a specific key was specified then we should already just have that {key:value} due to the above filtering,
    // but no need for the container object or the key, just return the value.
    if (key)
        return params[key];
    else
        return params; // Otherwise we return all the params.
}

export const url = {
    /**
     * Get all URL query params as a dictionary (object of key-value pairs).
     * 
     * @param {object} [opts] Parsing options, see getParams function for more info.
     */
    getAllParams(opts) {
        return this.getParams(null, opts);
    },
    /**
     * Get a single value for a specific query param (per the specified key).
     * 
     * @param {string} key The name of the query param you want the value for.
     * @param {object} [opts] Parsing options, see getParams function for more info. This call forces opts.multivar=false.
     */
    getParam(key, opts) {
        if (!key || !key.length)
            throw new Error('Invalid Argument: tools.url.getParam(key) argument "key" not provided.');

        return this.getParams(key, { ...opts, multival: false });
    },
    /**
     * Get an array of value(s) for a specific query param (per the specified key).
     * 
     * Although not common, multiple values can be specified for a query param.
     * If multiple values are passed as separate params with the same name (eg: ?p=1&p=2&p=3)
     * this function will get them all and return them in an array ([1,2,3]).
     * If multiple values are passed as a list of values for a single param (eg: ?p=1,2,3)
     * this is considered a single value and is left for you to parse as desired (eg: "1,2,3").
     * 
     * This function can be called without the key, and only the opts argument (which is itself optional)
     * and it will output an object of all params, this behaviour is identical to getAllParams.
     * 
     * Optional parsing options include:
     * {
     *   multival:boolean = If false then the array of values will be replaced with the first value, eg [1,2,3] => 1
     *   cast:type = If a type is specified (Number,Boolean,etc) then an attempt to cast all the values into that type will be made.
     * }
     * 
     * @param {string} key The name of the query param you want the value(s) for.
     * @param {object} [opts] Parsing options, see above for more info.
     */
    getParams(key, opts) {
        // Get the parsed params (caches the values and re-use when possible).
        const params = _cachedParse(_cached.query, location.search.slice(1)); // Slice off the first char (?).
        return _getParams(params, key, opts);
    },

    /**
     * Get all URL hash params as a dictionary (object of key-value pairs).
     * 
     * @param {object} [opts] Parsing options, see getHashParams function for more info.
     */
    getAllHashParams(opts) {
        return this.getHashParams(null, opts);
    },
    /**
     * Get a single value for a specific hash param (per the specified key).
     * 
     * @param {string} key The name of the hash param you want the value for.
     * @param {object} [opts] Parsing options, see getHashParams function for more info. This call forces opts.multivar=false.
     */
    getHashParam(key, opts) {
        if (!key || !key.length)
            throw new Error('Invalid Argument: tools.url.getHashParam(key) argument "key" not provided.');

        return this.getHashParams(key, { ...opts, multival: false });
    },
    /**
     * Get an array of value(s) for a specific hash param (per the specified key).
     * 
     * Although not common, multiple values can be specified for a hash param.
     * If multiple values are passed as separate params with the same name (eg: #p=1&p=2&p=3)
     * this function will get them all and return them in an array ([1,2,3]).
     * If multiple values are passed as a list of values for a single param (eg: #p=1,2,3)
     * this is considered a single value and is left for you to parse as desired (eg: "1,2,3").
     * 
     * This function can be called without the key, and only the opts argument (which is itself optional)
     * and it will output an object of all params, this behaviour is identical to getAllHashParams.
     * 
     * Optional parsing options include:
     * {
     *   multival:boolean = If false then the array of values will be replaced with the first value, eg [1,2,3] => 1
     *   cast:type = If a type is specified (Number,Boolean,etc) then an attempt to cast all the values into that type will be made.
     * }
     * 
     * @param {string} key The name of the hash param you want the value(s) for.
     * @param {object} [opts] Parsing options, see above for more info.
     */
    getHashParams(key, opts) {
        // Get the parsed params (caches the values and re-use when possible).
        const params = _cachedParse(_cached.hash, location.hash.slice(1)); // Slice off the first char (#).
        return _getParams(params, key, opts);
    },

    /**
     * Convenience function that takes an object of properties (key:value pairs) representing query params
     * and intelligently appending them onto an existing base URL. The base URL is checked appropriatley for
     * query '?' and '&' so the specified properties are appended correctly, however the base URL is not
     * modified itself, ie if a param already exists it is not replaced.
     * 
     * EXAMPLE:
     * assemble('base/url.html?a=1', { x: 5, y: '6', z: true, v: null }) == 'base/url.html?a=1&x=5&y=6&z=true'
     * 
     * @param {string|Array} url The base URL that will be appended to, can be a string or an array.
     *                           If an array it will be stripped of null/undefined elements and joined into a string separated by slashes.
     *                           Eg: ['a', null, 3, 'foo', '', 'bar'] -> 'a/3/foo//bar'
     *                           If an array and one (or more) of the elements is an Object (including Array) then the object's properties/elements
     *                           will be added to the params object as query string parameters.
     *                           Eg: ['a', 3, 'foo', { x:1, y:2, z:[4,5,6] }] -> 'a/3/foo?x=1&y=2&z=4&z=5&z=6
     * @param {object} params The object of (key:value) properties that will be appended as URL query params,
     *                        if values are null/undefined then they will be ignored.
     */
    assemble(url, params) {
        if (!url || !url.length)
            return null;
        else if (!params && typeof (url) === 'string') // Short-circuit for common situations where we don't need to do anything complicated.
            return url;

        // If url is an array then we process its elements into url segments and params.
        if (Array.isArray(url)) {
            params = params || {}; // Ensure params exists, as we may be adding key-values below.

            url = url.filter(element => {
                if (element === null || element === undefined)
                    return false; // DO NOT use null/undefined values (filtered out).
                else if (element instanceof Object) {
                    for (const k in element) {
                        if (element.hasOwnProperty(k)) {
                            params[k] = element[k];
                        }
                    }

                    return false; // Has been processed into params, DO NOT use in url (filtered out).
                }

                return true; // Otherwise use the value in the url (filtered in).
            });

            // Join the filtered url parts into path segments.
            url = url.join('/');
        }

        const paramKeys = params && Object.keys(params);
        if (paramKeys && paramKeys.length) {
            // Add query-string separator.
            if (url.indexOf('?') < 0)
                url += '?';
            else if (url[url.length - 1] !== '&')
                url += '&';

            // Add query-string params.
            for (const name of paramKeys) {
                if (params.hasOwnProperty(name)) {
                    let value = params[name];

                    // Don't include properties without values.
                    if (value !== null && value !== void 0) {
                        if (Array.isArray(value))
                            value = value.map(encodeURIComponent).join(`&${name}=`);
                        else if (value instanceof Object)
                            value = encodeURIComponent(JSON.stringify(value));
                        else
                            value = encodeURIComponent(value);

                        url += `${name}=${value}&`;
                    }
                }
            }

            // Trim off trailing ampersand.
            if (url[url.length - 1] === '&') {
                url = url.substring(0, url.length - 1);
            }
        }

        return url;
    },

    /**
     * Convenience function to format a root-relative URL in our standardized 'friendly' format.
     * 
     * EXAMPLE
     * createFriendlyUrl('auctioneer', 123, 'EZ Auctions') == '/auctioneer/123/EZ+Auctions'
     * 
     * @param {string} code The application specific view 'code', ie the first portion of the path (generally the view name).
     * @param {number} id The view object identifier, ie the primary key for the object the view will display/represent.
     * @param {string} words The SEO (and user) friendly words to append to the URL.
     */
    createFriendlyUrl(code, id, words) {
        // IMPORTANT: This 'friendly' url assembly/formatting should be the same as in LAG.Utility.URL.Friendly 
        if (words)
        {
            // Normalize to lowercase.
            words = words.toLowerCase();

            // Eliminate anything that is NOT letters, numbers, spaces or dashes (replace with spaces)
            words = words.replace(/[^-a-zA-Z0-9\s]/g, ' ');

            // Remove leading/tailing spaces.
            words = words.trim();

            // Replace (one or more) spaces with a single dash.
            words = words.replace(/\s+/g, '-');

            // Replace (one or more) dashes with a single dash.
            words = words.replace(/-+/g, '-');
        }

        // Return generated 'friendly' (root-relative) url: /<code>/<id>/<words>
        return `/${code}/${id}/${words}`;
    }
};